import * as React from 'react';
import ModalHeader from './layout/modal/ModalHeader';
import Modal from './layout/modal/Modal';
import Msg from './localization/Msg';
import { btnStyle } from './forms/styles/button';
import { COLORS } from './globalStyle/colors';
import LocalBtn from './localization/LocalBtn';
import LocalDiv from './localization/LocalDiv';
import { FLEX_COLUMN, FLEX_ROW } from './layout/flexBox';
import { css } from '@emotion/core';
import Spacer from './layout/Spacer';
import { onPhone, onMobileMedium } from './globalStyle/mediaQueries';
import { Str } from './localization/translationKeys';
import { useAppState } from './core/redux/hooks';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logOutAction } from './auth/login/api/logoutAction';
import { isOnMobile } from './language/browser';
import { PortalType } from './navigation/portalType';
import { makeRoutes } from './navigation/routes';
import { useMemo, useCallback, useEffect } from 'react';
import { showTimeoutModalAction } from './showTimeoutModalAction';
import { showTimedOutAction } from './showTimedOutAction';
import { TIME } from './language/time';
import { setTimedOutHandleAction } from './setTimedOutHandleAction';
import { useBrowser } from './language/useBrowser';


const styles = {
  modal:        css`
    min-height: 300px;
    min-width: 300px;
    max-width: 560px;
    margin: 40px;
    ${onPhone} {
      margin: 0;
    }
  `,
  content:      css`
    flex-grow: 1;
    margin: 0 20px 20px;
  `,
  msgContainer: css`
    margin: auto 90px;
    flex-grow: 1;
    justify-content: center;
    ${onMobileMedium} {
      margin: auto 40px;
    }
    ${onPhone} {
      margin-bottom: 60px;
    }
  `,
  msg:          css`
    font-weight: bold;
    font-size: 18px;
    line-height: 1.5;
  `,
  btnRow:       css`
    width: 100%;
    flex-grow: 0;
    margin-top: auto;
    justify-content: center;
  `,
  btn:          css`
    min-height: 35px;
    min-width: 132px;
  `,
};

type Props = {
  className?: string;
};


/**
 * TimeoutModal: a component that shows timeout messages
 *
 * PureComponent: true
 * Redux Connected:
 */
const TimeoutModal = () => {

  const showModal      = useAppState(state => state.showTimeoutModal);
  const timedOut       = useAppState(state => state.showTimedOut);
  const timedOutHandle = useAppState(state => state.timedOutHandle);

  const history  = useHistory();
  const dispatch = useDispatch();

  const logoutClick = useCallback(
    () => {
      dispatch(logOutAction(isOnMobile(window)));
      timedOutHandle && window.clearTimeout(timedOutHandle);
  
      if (history.location.pathname.toLowerCase().indexOf(PortalType.RATER_APPROVAL.toLowerCase()) > -1) {
        history.replace(makeRoutes().RaterApprovalLogin);
      } else if (history.location.pathname.toLowerCase().indexOf(PortalType.REPORT_RECIPIENT.toLowerCase()) > -1) {
        history.replace(makeRoutes().ReportRecipientLogin);
      } else {
        history.replace(makeRoutes().Login);
      }
    },
    [dispatch, history, timedOutHandle],
  );

  const { onIe } = useBrowser();
  
  const continueClick = useCallback(
    () => {
      dispatch(showTimeoutModalAction(false));
      timedOutHandle && window.clearTimeout(timedOutHandle);
    },
    [dispatch, timedOutHandle],
  ); 

  const dismissAction = timedOut ? logoutClick : continueClick;

  const setTimedOut = useCallback(
    () => {
      dispatch(showTimedOutAction(true));
    },
    [dispatch],
  );  
  
  useEffect(
    () => {
      if (showModal) {
        const handle = window.setTimeout(setTimedOut, 5 * TIME.M);
        dispatch(setTimedOutHandleAction(handle));
      }
    },
    [showModal, setTimedOut, dispatch],
  );

  const element = useMemo(() => 
    showModal || timedOut
    ? <Modal 
        css={[FLEX_COLUMN, styles.modal]}
        onDismiss={dismissAction}
      >
        <ModalHeader onDismiss={dismissAction} />
        <LocalDiv css={[FLEX_COLUMN, styles.content]}>
          <div css={[FLEX_COLUMN, styles.msgContainer]}>
            <Msg css={[styles.msg, onIe ? css`padding: 65px 0;` : undefined]}
              str={timedOut ? Str.TimedOutMsg : Str.TimeoutMsg}
              replace={{ 'number': 5 }}
            />
          </div>
          <LocalDiv css={[FLEX_ROW, styles.btnRow]}>
            <div css={css`flex-basis: 100%;`} />
            {
              timedOut
              ? <LocalBtn
                  str={Str.Ok}
                  css={[btnStyle.primary(COLORS.BLUE), styles.btn]}
                  onClick={logoutClick}
                />
              : <>
                  <LocalBtn
                    str={Str.LogOut}
                    css={[btnStyle.transparent(COLORS.BLUE), styles.btn]}
                    onClick={logoutClick}
                  />
                  <Spacer sizePx={10} />
                  <LocalBtn
                    str={Str.Continue}
                    css={[btnStyle.primary(COLORS.BLUE), styles.btn]}
                    onClick={continueClick}
                  />
                </>
            }
          </LocalDiv>
        </LocalDiv>
      </Modal>
    : null,
    [showModal, timedOut, onIe, continueClick, logoutClick, dismissAction],
  );

  return element;
};

export default TimeoutModal;
